import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import PageHero from '../components/PageHero';
import { logout } from '../redux/authSlice';
import apiClient from '../utils/apiClient';

const TIER_LIMITS = { free: 3, starter: 18, pro: 60, agency: 999999 };
const TIER_LABELS = { free: 'Free', starter: 'Starter', pro: 'Pro', agency: 'Agency' };

function normalizeModelForProvider(provider, model) {
  if (!model) return '';
  if (provider === 'openai') {
    return model.replace(/^openai\//, '');
  }
  if (provider === 'openrouter' && !model.includes('/') && model.startsWith('gpt-')) {
    return `openai/${model}`;
  }
  return model;
}

function getProviderModelOptions(settings, provider) {
  const available = settings?.available_models?.[provider];
  if (Array.isArray(available) && available.length > 0) {
    return available.map(entry => {
      if (typeof entry === 'string') {
        return { value: entry, label: entry, meta: '' };
      }
      return {
        value: entry.id,
        label: entry.name || entry.id,
        meta: entry.name ? entry.id : '',
      };
    });
  }

  const recommended = settings?.recommended_models?.[provider] || [];
  return recommended.map(model => ({ value: model, label: model, meta: '' }));
}

function TierBadge({ tier, isLifetime }) {
  if (isLifetime) {
    return (
      <span style={{
        background: 'rgba(34,197,94,0.15)', color: '#22C55E',
        border: '1px solid rgba(34,197,94,0.35)', borderRadius: 999,
        padding: '3px 10px', fontSize: 11, fontWeight: 700,
        letterSpacing: '0.06em', textTransform: 'uppercase',
      }}>
        ♾ Lifetime {TIER_LABELS[tier] || tier}
      </span>
    );
  }
  const styles = {
    free: { background: 'rgba(107,114,128,0.2)', color: '#9CA3AF', border: '1px solid rgba(107,114,128,0.3)' },
    starter: { background: 'rgba(139,92,246,0.15)', color: '#A78BFA', border: '1px solid rgba(139,92,246,0.35)' },
    pro: { background: 'rgba(249,115,22,0.15)', color: '#F97316', border: '1px solid rgba(249,115,22,0.35)' },
    agency: { background: 'rgba(30,58,138,0.3)', color: '#93C5FD', border: '1px solid rgba(30,58,138,0.5)' },
  };
  const s = styles[tier] || styles.free;
  return (
    <span style={{
      ...s, borderRadius: 999, padding: '3px 10px', fontSize: 11,
      fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
    }}>
      {TIER_LABELS[tier] || tier}
    </span>
  );
}

export default function TheIdentity() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(s => s.auth.user);
  const [searchParams, setSearchParams] = useSearchParams();

  const [settings, setSettings] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [keyInput, setKeyInput] = useState('');
  const [llmDraft, setLlmDraft] = useState({
    provider: 'openai',
    baseUrl: '',
    modelDefault: '',
    headers: { 'HTTP-Referer': '', 'X-Title': '' },
    agentOverrides: {},
  });
  const [llmState, setLlmState] = useState('idle'); // idle | saving | saved | error
  const [llmMsg, setLlmMsg] = useState('');
  const [showAdvancedLlm, setShowAdvancedLlm] = useState(false);
  const [showAgentOverrides, setShowAgentOverrides] = useState(false);

  const [upgradeLoading, setUpgradeLoading] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    Promise.all([
      apiClient.get('/settings/').catch(() => null),
      apiClient.get('/stripe/status/').catch(() => null),
      apiClient.get('/agents/list/').catch(() => null),
    ]).then(([s, st, a]) => {
      if (s) {
        setSettings(s.data);
        const savedOverrides = s.data.llm_agent_model_overrides || {};
        setLlmDraft({
          provider: s.data.llm_provider || 'openai',
          baseUrl: s.data.llm_base_url || '',
          modelDefault: s.data.llm_model_default || '',
          headers: {
            'HTTP-Referer': s.data.llm_headers?.['HTTP-Referer'] || '',
            'X-Title': s.data.llm_headers?.['X-Title'] || '',
          },
          agentOverrides: savedOverrides,
        });
        setShowAdvancedLlm(Boolean(s.data.has_llm_key || s.data.llm_base_url || Object.keys(s.data.llm_headers || {}).length));
        setShowAgentOverrides(Boolean(Object.keys(savedOverrides).length));
      }
      if (st) setStripeStatus(st.data);
      if (a) setAgents(Array.isArray(a.data) ? a.data : []);
      setLoading(false);

      const planParam = searchParams.get('plan');
      if (planParam) {
        setSearchParams({}, { replace: true });
        handleUpgrade(planParam);
      }
    });
  }, [searchParams, setSearchParams]);

  function handleLogout() {
    dispatch(logout());
    localStorage.removeItem('multiauthor_token');
    navigate('/login');
  }

  async function handleSaveLlmSettings() {
    setLlmState('saving');
    setLlmMsg('');
    try {
      const payload = {
        llm_provider: llmDraft.provider,
        llm_base_url: llmDraft.baseUrl.trim(),
        llm_model_default: llmDraft.modelDefault,
        llm_headers: Object.fromEntries(
          Object.entries(llmDraft.headers || {}).filter(([, value]) => (value || '').trim())
        ),
        llm_agent_model_overrides: Object.fromEntries(
          Object.entries(llmDraft.agentOverrides || {}).filter(([, value]) => Boolean(value))
        ),
      };
      const maybeKey = keyInput.trim();
      if (maybeKey) {
        payload.llm_api_key = maybeKey;
      }
      const r = await apiClient.patch('/settings/', payload);
      setSettings(prev => ({
        ...prev,
        llm_provider: llmDraft.provider,
        llm_base_url: llmDraft.baseUrl.trim(),
        llm_model_default: llmDraft.modelDefault,
        llm_headers: payload.llm_headers,
        llm_agent_model_overrides: payload.llm_agent_model_overrides,
        has_llm_key: r.data.has_llm_key,
        llm_key_preview: r.data.llm_key_preview,
        has_openai_key: r.data.has_openai_key,
        openai_key_preview: r.data.openai_key_preview,
      }));
      setKeyInput('');
      setLlmState('saved');
    } catch {
      setLlmState('error');
      setLlmMsg('Failed to save LLM settings.');
    }
  }

  async function handleRemoveKey() {
    setLlmState('saving');
    try {
      await apiClient.patch('/settings/', { action: 'remove' });
      setSettings(prev => ({
        ...prev,
        has_llm_key: false,
        llm_key_preview: null,
        has_openai_key: false,
        openai_key_preview: null,
      }));
      setKeyInput('');
      setLlmState('idle');
      setLlmMsg('');
    } catch {
      setLlmState('error');
      setLlmMsg('Failed to remove key.');
    }
  }

  async function handleUpgrade(tier) {
    setUpgradeLoading(tier);
    try {
      const r = await apiClient.post('/stripe/checkout/', { tier });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch {
      setUpgradeLoading('');
    }
  }

  const modelOptions = getProviderModelOptions(settings, llmDraft.provider);
  const displayName = user?.username || 'User';
  const initials = displayName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) || 'U';
  const tier = stripeStatus?.subscription_tier || settings?.subscription_tier || 'free';
  const isLifetime = stripeStatus?.is_lifetime || false;
  const totalProjects = stripeStatus?.total_projects ?? 0;
  const completedProjects = stripeStatus?.completed_projects ?? 0;
  const workflowsThisMonth = stripeStatus?.workflows_this_month ?? totalProjects;
  const limit = stripeStatus?.workflow_limit ?? TIER_LIMITS[tier] ?? 3;
  const remaining = stripeStatus?.workflows_remaining ?? Math.max(0, limit - workflowsThisMonth);
  const isUnlimited = limit >= 999999;
  const usagePct = isUnlimited ? 0 : Math.min((workflowsThisMonth / limit) * 100, 100);
  const selectedDefaultModel = modelOptions.find(option => option.value === llmDraft.modelDefault);

  const page = {
    maxWidth: 680, margin: '0 auto', padding: isMobile ? '20px 16px 24px' : '24px',
    fontFamily: 'Inter, sans-serif',
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
  };
  const card = {
    background: '#1A2236', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 16, padding: isMobile ? 16 : 20,
    boxSizing: 'border-box',
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
  };
  const inputStyle = {
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 10, padding: '11px 14px', color: '#F1F5F9', fontSize: 14,
    outline: 'none', fontFamily: 'Inter, sans-serif', flex: 1,
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
  };

  if (loading) {
    return (
      <div style={page}>
        <PageHero badge="PROFILE" title="The Identity" subtitle="Loading your profile..." />
        <div style={{ textAlign: 'center', color: '#6B7280', padding: 40 }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={page}>
      <PageHero
        badge="PROFILE"
        title="The Identity"
        subtitle="Your personal space. Manage your profile, subscription, and API settings."
      />

      {/* ── Section 1: Profile Header ────────────────── */}
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: isMobile ? 14 : 20, marginBottom: 20, minWidth: 0 }}>
        <div style={{
          width: 56, height: 56, borderRadius: 14, background: '#1E3A8A', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontSize: 20, fontWeight: 800, fontFamily: 'Syne, sans-serif',
        }}>{initials}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: 10, marginBottom: 4, minWidth: 0 }}>
            <span style={{ fontWeight: 700, fontSize: 18, color: '#F1F5F9', fontFamily: 'Syne, sans-serif' }}>
              {displayName}
            </span>
            <TierBadge tier={tier} isLifetime={isLifetime} />
          </div>
          <div style={{ color: '#6B7280', fontSize: 13, overflowWrap: 'anywhere' }}>{user?.email || '—'}</div>
        </div>
      </div>

      {/* ── Section 2: Subscription ──────────────────── */}
      <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 16, color: '#F1F5F9', marginBottom: 12, marginTop: 28 }}>
        Subscription
      </h2>
      <div style={{ ...card, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: 12, marginBottom: 16 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#F1F5F9', marginBottom: 2 }}>
              {isLifetime ? `Lifetime ${TIER_LABELS[tier] || tier}` : `${TIER_LABELS[tier] || tier} Plan`}
            </div>
            <div style={{ color: '#6B7280', fontSize: 13 }}>
              {isUnlimited
                ? `${workflowsThisMonth} workflows this month`
                : `${workflowsThisMonth} of ${limit} workflows used this month`}
            </div>
          </div>
          <TierBadge tier={tier} isLifetime={isLifetime} />
        </div>

        {!isUnlimited && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 999 }}>
              <div style={{
                height: '100%', width: `${usagePct}%`, borderRadius: 999,
                background: usagePct >= 90 ? '#F87171' : '#F97316',
                transition: 'width 0.4s ease',
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
              <span style={{ color: '#6B7280', fontSize: 12 }}>{workflowsThisMonth} used</span>
              <span style={{ color: remaining <= 2 ? '#F87171' : '#6B7280', fontSize: 12 }}>{remaining} remaining</span>
            </div>
          </div>
        )}

        {/* Upgrade buttons — show based on current tier */}
        {!isLifetime && tier === 'free' && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {[
              { key: 'starter', label: 'Starter — $9/mo', bg: '#6D28D9' },
              { key: 'pro', label: 'Pro — $29/mo', bg: '#F97316' },
              { key: 'agency', label: 'Agency — $79/mo', bg: '#1E3A8A' },
            ].map(({ key, label, bg }) => (
              <button
                key={key}
                onClick={() => handleUpgrade(key)}
                disabled={upgradeLoading === key}
                style={{
                  flex: 1, minWidth: 120, padding: '10px 0', background: bg,
                  color: 'white', border: 'none', borderRadius: 10, fontWeight: 700,
                  fontSize: 13, cursor: 'pointer', opacity: upgradeLoading === key ? 0.7 : 1,
                  fontFamily: 'Syne, sans-serif',
                }}
              >
                {upgradeLoading === key ? 'Redirecting...' : `↑ ${label}`}
              </button>
            ))}
          </div>
        )}

        {!isLifetime && tier === 'starter' && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {[
              { key: 'pro', label: 'Upgrade to Pro — $29/mo', bg: '#F97316' },
              { key: 'agency', label: 'Upgrade to Agency — $79/mo', bg: '#1E3A8A' },
            ].map(({ key, label, bg }) => (
              <button
                key={key}
                onClick={() => handleUpgrade(key)}
                disabled={upgradeLoading === key}
                style={{
                  flex: 1, minWidth: 140, padding: '11px 0', background: bg,
                  color: 'white', border: 'none', borderRadius: 10, fontWeight: 700,
                  fontSize: 14, cursor: 'pointer', opacity: upgradeLoading === key ? 0.7 : 1,
                  fontFamily: 'Syne, sans-serif',
                }}
              >
                {upgradeLoading === key ? 'Redirecting...' : label}
              </button>
            ))}
          </div>
        )}

        {!isLifetime && tier === 'pro' && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              onClick={() => handleUpgrade('agency')}
              disabled={upgradeLoading === 'agency'}
              style={{
                padding: '11px 20px', background: '#1E3A8A', color: 'white',
                border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14,
                cursor: 'pointer', opacity: upgradeLoading === 'agency' ? 0.7 : 1,
                fontFamily: 'Syne, sans-serif',
              }}
            >
              {upgradeLoading === 'agency' ? 'Redirecting...' : '↑ Upgrade to Agency — $79/mo'}
            </button>
            <button
              onClick={() => handleUpgrade(tier)}
              style={{
                background: 'transparent', border: 'none', color: '#9CA3AF',
                fontSize: 13, cursor: 'pointer', padding: 0, textDecoration: 'underline',
              }}
            >
              Manage Billing →
            </button>
          </div>
        )}

        {!isLifetime && tier === 'agency' && (
          <button
            onClick={() => handleUpgrade(tier)}
            style={{
              background: 'transparent', border: 'none', color: '#9CA3AF',
              fontSize: 13, cursor: 'pointer', padding: 0, textDecoration: 'underline',
            }}
          >
            Manage Billing →
          </button>
        )}

        {isLifetime && (
          <div style={{
            background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)',
            borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#22C55E',
          }}>
            ♾ Lifetime access — no renewals, no limits.
          </div>
        )}
      </div>

      {/* ── Section 3: LLM Provider & Models ───────────────── */}
      <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 16, color: '#F1F5F9', marginBottom: 12, marginTop: 28 }}>
        LLM Provider & Models
      </h2>
      <div style={{ ...card, marginBottom: 20 }}>
        <div style={{ color: '#9CA3AF', fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>
          Most users only need two things here: a provider and one default model.
        </div>

        <div style={{ display: 'grid', gap: 14 }}>
          <label style={{ display: 'grid', gap: 6, color: '#E5E7EB', fontSize: 13 }}>
            <span>Provider</span>
            <select
              aria-label="Provider"
              value={llmDraft.provider}
              onChange={e => {
                const provider = e.target.value;
                const nextModels = getProviderModelOptions(settings, provider).map(option => option.value);
                setLlmDraft(prev => ({
                  ...prev,
                  provider,
                  modelDefault: (() => {
                    const candidate = normalizeModelForProvider(provider, prev.modelDefault);
                    return nextModels.includes(candidate) ? candidate : (nextModels[0] || '');
                  })(),
                  agentOverrides: Object.fromEntries(
                    Object.entries(prev.agentOverrides || {}).map(([name, value]) => {
                      const candidate = normalizeModelForProvider(provider, value);
                      return [name, nextModels.includes(candidate) ? candidate : ''];
                    })
                  ),
                }));
                setLlmState('idle');
                setLlmMsg('');
              }}
              style={inputStyle}
            >
              <option value="openai">OpenAI</option>
              <option value="openrouter">OpenRouter</option>
            </select>
          </label>

          <label style={{ display: 'grid', gap: 6, color: '#E5E7EB', fontSize: 13 }}>
            <span>Default Model</span>
            <select
              aria-label="Default Model"
              value={llmDraft.modelDefault}
              onChange={e => {
                setLlmDraft(prev => ({ ...prev, modelDefault: e.target.value }));
                setLlmState('idle');
              }}
              style={inputStyle}
            >
              <option value="">Select a model</option>
              {modelOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>

          {selectedDefaultModel?.meta && (
            <div style={{ color: '#94A3B8', fontSize: 12, marginTop: -4, overflowWrap: 'anywhere' }}>
              Model ID: {selectedDefaultModel.meta}
            </div>
          )}

          <div style={{ color: '#9CA3AF', fontSize: 12, lineHeight: 1.6 }}>
            This is the model used across your workflow unless you customise a specific agent.
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
            <button
              type="button"
              onClick={() => setShowAgentOverrides(prev => !prev)}
              style={{
                padding: '10px 14px', background: 'transparent', color: '#F1F5F9',
                border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, fontWeight: 600,
                fontSize: 13, cursor: 'pointer', width: isMobile ? '100%' : 'auto', textAlign: 'left',
              }}
            >
              {showAgentOverrides ? 'Hide agent-specific models' : 'Customise individual agents'}
            </button>
            <button
              type="button"
              onClick={() => setShowAdvancedLlm(prev => !prev)}
              style={{
                padding: '10px 14px', background: 'transparent', color: '#9CA3AF',
                border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, fontWeight: 600,
                fontSize: 13, cursor: 'pointer', width: isMobile ? '100%' : 'auto', textAlign: 'left',
              }}
            >
              {showAdvancedLlm ? 'Hide connection settings' : 'Connection settings'}
            </button>
          </div>

          {showAdvancedLlm && (
            <>
              <label style={{ display: 'grid', gap: 6, color: '#E5E7EB', fontSize: 13 }}>
                <span>API Key</span>
                <input
                  aria-label="API Key"
                  type="password"
                  placeholder={llmDraft.provider === 'openrouter' ? 'sk-or-...' : 'sk-...'}
                  value={keyInput}
                  onChange={e => {
                    setKeyInput(e.target.value);
                    setLlmState('idle');
                    setLlmMsg('');
                  }}
                  style={inputStyle}
                />
              </label>

              {(settings?.has_llm_key || settings?.has_openai_key) && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22C55E', flexShrink: 0 }} />
                    <span style={{ color: '#22C55E', fontSize: 14, fontWeight: 600 }}>
                      Key saved: {settings?.llm_key_preview || settings?.openai_key_preview}
                    </span>
                  </div>
                  <button
                    onClick={handleRemoveKey}
                    disabled={llmState === 'saving'}
                    style={{
                      padding: '9px 20px', background: 'transparent',
                      color: '#F87171', border: '1px solid rgba(248,113,113,0.35)',
                      borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      opacity: llmState === 'saving' ? 0.6 : 1,
                    }}
                  >
                    {llmState === 'saving' ? 'Removing...' : 'Remove Key'}
                  </button>
                </div>
              )}

              <label style={{ display: 'grid', gap: 6, color: '#E5E7EB', fontSize: 13 }}>
                <span>Base URL</span>
                <input
                  aria-label="Base URL"
                  type="text"
                  value={llmDraft.baseUrl}
                  onChange={e => {
                    setLlmDraft(prev => ({ ...prev, baseUrl: e.target.value }));
                    setLlmState('idle');
                  }}
                  placeholder={llmDraft.provider === 'openrouter' ? 'https://openrouter.ai/api/v1' : 'https://api.openai.com/v1'}
                  style={inputStyle}
                />
              </label>

              {llmDraft.provider === 'openrouter' && (
                <div style={{ display: 'grid', gap: 14 }}>
                  <label style={{ display: 'grid', gap: 6, color: '#E5E7EB', fontSize: 13 }}>
                    <span>HTTP-Referer</span>
                    <input
                      aria-label="HTTP-Referer"
                      type="text"
                      value={llmDraft.headers['HTTP-Referer'] || ''}
                      onChange={e => {
                        const value = e.target.value;
                        setLlmDraft(prev => ({
                          ...prev,
                          headers: { ...prev.headers, 'HTTP-Referer': value },
                        }));
                        setLlmState('idle');
                      }}
                      placeholder="https://app.example.com"
                      style={inputStyle}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: 6, color: '#E5E7EB', fontSize: 13 }}>
                    <span>X-Title</span>
                    <input
                      aria-label="X-Title"
                      type="text"
                      value={llmDraft.headers['X-Title'] || ''}
                      onChange={e => {
                        const value = e.target.value;
                        setLlmDraft(prev => ({
                          ...prev,
                          headers: { ...prev.headers, 'X-Title': value },
                        }));
                        setLlmState('idle');
                      }}
                      placeholder="Multi Author"
                      style={inputStyle}
                    />
                  </label>
                </div>
              )}
            </>
          )}

          {showAgentOverrides && (
            <div>
              <div style={{ color: '#F1F5F9', fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
                Agent model overrides
              </div>
              <div style={{ color: '#9CA3AF', fontSize: 12, marginBottom: 12, lineHeight: 1.6 }}>
                Optional: pick a different model for any specific agent. Leave it blank to use your default model.
              </div>
              {agents.length === 0 ? (
                <div style={{ color: '#FBBF24', fontSize: 12 }}>
                  Agent list is not available right now.
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 12 }}>
                  {agents.map(agent => (
                    <label key={agent.id || agent.name} style={{ display: 'grid', gap: 6, color: '#E5E7EB', fontSize: 13 }}>
                      <span>{agent.name} model override</span>
                      <select
                        aria-label={`${agent.name} model override`}
                        value={llmDraft.agentOverrides?.[agent.name] || ''}
                        onChange={e => {
                          const value = e.target.value;
                          setLlmDraft(prev => ({
                            ...prev,
                            agentOverrides: {
                              ...(prev.agentOverrides || {}),
                              [agent.name]: value,
                            },
                          }));
                          setLlmState('idle');
                        }}
                        style={inputStyle}
                      >
                        <option value="">Use default model for this agent</option>
                        {modelOptions.map(option => (
                          <option key={`${agent.name}-${option.value}`} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
            <button
              onClick={handleSaveLlmSettings}
              disabled={llmState === 'saving'}
              style={{
                padding: '11px 20px', background: '#F97316', color: 'white',
                border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14,
                cursor: 'pointer', opacity: llmState === 'saving' ? 0.6 : 1, width: isMobile ? '100%' : 'auto',
              }}
            >
              {llmState === 'saving' ? 'Saving...' : 'Save LLM Settings'}
            </button>
            {llmState === 'error' && (
              <p style={{ color: '#F87171', fontSize: 12, margin: '10px 0 0' }}>{llmMsg}</p>
            )}
          </div>
        </div>
      </div>

      {/* ── Section 4: Usage Stats ────────────────────── */}
      <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 16, color: '#F1F5F9', marginBottom: 12, marginTop: 28 }}>
        Usage
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, marginBottom: 36 }}>
        {[
          {
            value: totalProjects.toString(),
            label: 'Total Projects',
          },
          {
            value: completedProjects.toString(),
            label: 'Completed',
          },
          {
            value: isUnlimited ? '∞' : remaining.toString(),
            label: 'Remaining',
          },
        ].map(({ value, label }) => (
          <div key={label} style={{ ...card, textAlign: 'center', padding: 16 }}>
            <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 28, color: '#F1F5F9' }}>
              {value}
            </div>
            <div style={{ color: '#9CA3AF', fontSize: 12, marginTop: 4 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Sign Out */}
      <div style={{ textAlign: 'center', paddingBottom: 8 }}>
        <button
          onClick={handleLogout}
          style={{
            padding: '10px 24px', background: 'transparent',
            color: '#F87171', border: '1px solid rgba(248,113,113,0.3)',
            borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 8,
          }}
        >
          <LogOut size={16} />
          Sign Out
        </button>

        <div style={{ marginTop: 16 }}>
          <button
            onClick={() => navigate('/delete-account')}
            style={{
              background: 'none', border: 'none', padding: 0,
              color: '#6B7280', fontSize: 13, cursor: 'pointer',
              textDecoration: 'underline', fontFamily: 'inherit',
            }}
          >
            Delete my account
          </button>
        </div>
      </div>
    </div>
  );
}
